import { useNavigate } from "react-router-dom";

interface InventoryFooterProps {
    isLoading?: boolean;
    currentStep?: number;
    onBackClick?: any;
    onFinishClick?: any;
}

interface StepCircleProps {
    number: any;
    isActive: any;
}

function InventoryFooter({ isLoading = false, currentStep = 1, onBackClick, onFinishClick }: InventoryFooterProps) {
    const navigate = useNavigate();

    const handleBackClick = () => {
        if (onBackClick) {
            onBackClick();
        } else {
            navigate("/");
        }
    };

    const handleFinishClick = () => {
        if (onFinishClick) {
            onFinishClick();
        }
    };

    const StepCircle = ({ number, isActive }: StepCircleProps) => {
        const showCompletedTick = number === '1' && currentStep === 2;

        if (showCompletedTick) {
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="opacity-100">
                    <circle cx="12" cy="12" r="11" fill="#0F172A" />
                    <path d="M18 7L10 15L6 11" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        }

        return (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={`${isActive ? 'opacity-100' : 'opacity-50'}`}>
                <circle cx="12" cy="12" r="11" fill="#F1F5F9" stroke="#64748B" strokeWidth="2" />
                <text x="12" y="16" textAnchor="middle" fontSize="12" fontWeight={isActive ? "600" : "400"} fill="#64748B">
                    {number}
                </text>
            </svg>
        );
    };

    const leftButtonLabel = typeof (InventoryFooter as any).leftButtonLabel === 'string' ? (InventoryFooter as any).leftButtonLabel : undefined;
    const rightButtonLabel = typeof (InventoryFooter as any).rightButtonLabel === 'string' ? (InventoryFooter as any).rightButtonLabel : undefined;
    const backLabel = (typeof onBackClick === 'function' && typeof (InventoryFooter as any).leftButtonLabel === 'string') ? (InventoryFooter as any).leftButtonLabel : (currentStep === 2 ? 'Decide on Recommendations' : 'Back to Tasks');
    const finishLabel = (typeof onFinishClick === 'function' && typeof (InventoryFooter as any).rightButtonLabel === 'string') ? (InventoryFooter as any).rightButtonLabel : (currentStep === 2 ? 'Finalize Recommendations' : 'Review Recommendations');

    return (
        <div
            className="sticky top-0 bg-white border-b border-slate-200 h-12 flex items-center justify-between px-0 z-10 shadow-sm mb-4"
        >
            {/* Left button */}
            <button
                onClick={handleBackClick}
                className="flex items-center gap-2 h-full px-4 rounded-none transparent hover:bg-slate-200 transition-colors"
                disabled={isLoading}
            >
                <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 9L1 5L5 1" stroke="#111827" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-sm font-medium text-slate-900">{backLabel}</span>
            </button>

            {/* Centered Stepper - 2 Steps */}
            <div className="flex-1 flex items-center justify-center gap-4">
                <div className="flex items-center gap-1">
                    <StepCircle number="1" isActive={currentStep === 1} />
                    <span className={`text-xs ml-1 ${currentStep === 1 ? 'font-semibold text-slate-900' : (currentStep === 2 ? 'font-semibold text-slate-900' : 'font-normal text-slate-600 opacity-50')}`}>
                        Decide
                    </span>
                </div>

                <div className={`w-8 h-px ${currentStep === 1 ? 'bg-slate-300' : 'bg-slate-900'} transition-colors duration-200`} />

                <div className="flex items-center gap-1">
                    <StepCircle number="2" isActive={currentStep === 2} />
                    <span className={`text-xs ml-1 ${currentStep === 2 ? 'font-semibold text-slate-900' : 'font-normal text-slate-600 opacity-50'}`}>
                        Review
                    </span>
                </div>
            </div>

            {/* Right button */}
            <button
                onClick={handleFinishClick}
                className="flex items-center gap-2 h-full px-4 rounded-none bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
                disabled={isLoading}
            >
                <span className="text-sm font-medium">{finishLabel}</span>
                <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L5 5L1 9" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
}

export default InventoryFooter;
